/**
 * Airbnb-style map search view powered by Leaflet + MarkerCluster.
 *
 * Capabilities:
 *   - Renders all `items` as custom price-pill markers.
 *   - Clusters dense markers; clusters expand on zoom.
 *   - Emits `onBoundsChange(bbox)` after the user pans/zooms so the parent
 *     can refetch (bbox-scoped) AFTER the user confirms via a floating
 *     "Buscar en esta zona" button (Airbnb-style).
 *   - Calls `onHoverProperty(id|null)` to sync hover state with the list.
 *   - Click on a marker: opens a tiny preview card with photo + price +
 *     beds + area + "Ver propiedad" CTA.
 *
 * The Leaflet CSS + cluster CSS are loaded once on first mount.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X, Bed, Maximize } from 'lucide-react';
import { formatCOP, formatShortPrice } from '../mock';
import { Button } from './ui/button';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const CLUSTER_CSS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';
const CLUSTER_DEFAULT_CSS = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css';

let _libs;  // module-level cache for Leaflet + cluster

async function loadLibs() {
  if (_libs) return _libs;
  // Inject CSS once (Leaflet + cluster look broken without them).
  const links = [LEAFLET_CSS, CLUSTER_CSS, CLUSTER_DEFAULT_CSS];
  for (const href of links) {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
    }
  }
  const L = (await import('leaflet')).default;
  // markercluster patches the Leaflet namespace as a side-effect.
  await import('leaflet.markercluster');
  _libs = { L };
  return _libs;
}

/** Map a property to a custom HTML pill marker. */
function buildPriceIcon(L, p, isHovered) {
  const price = formatShortPrice(p.price, p.operation);
  const cls = isHovered
    ? 'bg-habita text-white border-habita scale-110 shadow-lg'
    : 'bg-white text-habita-dark border-habita hover:bg-habita-mint';
  const html = `
    <div class="habita-map-pill ${cls}" data-id="${p.id}">
      ${price}
    </div>
  `;
  return L.divIcon({
    className: 'habita-map-pin-wrapper',
    html,
    iconSize: null,    // size from CSS
    iconAnchor: [28, 14],
  });
}

export default function MapSearchView({
  items,
  loading,
  hoveredId,
  onHoverProperty,
  onBoundsChange,
  className = '',
  visible = true,   // when toggled to visible after being hidden, invalidate map size
}) {
  const navigate = useNavigate();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const markersById = useRef({});
  const [ready, setReady] = useState(false);
  const [showSearchHere, setShowSearchHere] = useState(false);
  const [popup, setPopup] = useState(null);   // { property, latlng }
  const lastFitKey = useRef(null);
  // Ref-based guard: when we programmatically fit/pan the map, ignore the
  // resulting moveend event so the "Buscar en esta zona" button does not
  // bounce back into view.
  const programmaticMoveUntil = useRef(0);

  // ── 1. Initial mount: load Leaflet + cluster, create map  ──────────────
  useEffect(() => {
    let cancelled = false;
    let leaveCleanup = () => {};

    loadLibs().then(({ L }) => {
      if (cancelled || !mapDivRef.current) return;

      const map = L.map(mapDivRef.current, {
        zoomControl: false,
        attributionControl: true,
        preferCanvas: true,
      }).setView([4.711, -74.0721], 6);  // default: centered on Colombia

      L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`, {
        maxZoom: 19,
        attribution: '© OpenStreetMap, © CARTO',
      }).addTo(map);

      L.control.zoom({ position: 'bottomright' }).addTo(map);

      const cluster = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        chunkedLoading: true,
        maxClusterRadius: 55,
        iconCreateFunction: (clusterObj) => {
          const count = clusterObj.getChildCount();
          const size = count < 10 ? 36 : count < 50 ? 44 : 52;
          return L.divIcon({
            html: `<div class="habita-map-cluster" style="width:${size}px;height:${size}px">${count}</div>`,
            className: 'habita-map-cluster-wrap',
            iconSize: [size, size],
          });
        },
      });
      map.addLayer(cluster);

      mapRef.current = map;
      clusterRef.current = cluster;
      setReady(true);

      const onMoveEnd = () => {
        // Skip events triggered by our own fitBounds / setView calls.
        if (Date.now() < programmaticMoveUntil.current) return;
        setShowSearchHere(true);
      };
      map.on('moveend zoomend', onMoveEnd);

      leaveCleanup = () => {
        map.off('moveend zoomend', onMoveEnd);
        map.remove();
        mapRef.current = null;
        clusterRef.current = null;
      };
    });

    return () => {
      cancelled = true;
      leaveCleanup();
    };
  }, []);

  // ── 2. Render markers whenever items change  ───────────────────────────
  useEffect(() => {
    if (!ready || !clusterRef.current || !mapRef.current) return;
    const cluster = clusterRef.current;
    const L = _libs.L;
    cluster.clearLayers();
    markersById.current = {};

    const newMarkers = [];
    for (const p of items) {
      if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue;
      const marker = L.marker([p.lat, p.lng], {
        icon: buildPriceIcon(L, p, false),
        riseOnHover: true,
      });
      marker.on('click', () => {
        setPopup({ property: p });
      });
      marker.on('mouseover', () => onHoverProperty?.(p.id));
      marker.on('mouseout', () => onHoverProperty?.(null));
      markersById.current[p.id] = marker;
      newMarkers.push(marker);
    }
    cluster.addLayers(newMarkers);

    // Auto-fit to data on first load OR when items signature changes
    // significantly (e.g. filter change). We avoid re-fitting on every
    // small list change to not yank the map under the user.
    const sig = items.map(p => p.id).sort().join(',');
    if (sig && sig !== lastFitKey.current && items.length > 0) {
      lastFitKey.current = sig;
      const valid = items.filter(p => typeof p.lat === 'number' && typeof p.lng === 'number');
      if (valid.length > 0) {
        const bounds = L.latLngBounds(valid.map(p => [p.lat, p.lng]));
        mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      }
      // The fit will trigger moveend → showSearchHere=true. Suppress it
      // because this is OUR fit, not a user action.
      setTimeout(() => setShowSearchHere(false), 200);
    }
  }, [items, ready, onHoverProperty]);

  // ── 3. Update hovered marker styling  ──────────────────────────────────
  useEffect(() => {
    if (!ready || !_libs?.L) return;
    const L = _libs.L;
    Object.entries(markersById.current).forEach(([id, marker]) => {
      const p = items.find(x => x.id === id);
      if (!p) return;
      marker.setIcon(buildPriceIcon(L, p, id === hoveredId));
    });
  }, [hoveredId, items, ready]);

  // ── 4. "Search this area" button handler  ──────────────────────────────
  const onSearchThisArea = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    onBoundsChange?.({
      west: b.getWest(),
      south: b.getSouth(),
      east: b.getEast(),
      north: b.getNorth(),
    });
    setShowSearchHere(false);
  }, [onBoundsChange]);

  // ── 5. Invalidate size when becoming visible after a hide/show toggle
  //       (mobile "Ver mapa" / "Ver lista"). Leaflet caches the container
  //       size at init; we need to tell it the size changed AND re-fit
  //       so the markers/clusters end up in view.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const t = setTimeout(() => {
      try {
        mapRef.current.invalidateSize();
        const L = _libs?.L;
        const valid = items.filter(p => typeof p.lat === 'number' && typeof p.lng === 'number');
        if (L && valid.length > 0) {
          const bounds = L.latLngBounds(valid.map(p => [p.lat, p.lng]));
          programmaticMoveUntil.current = Date.now() + 1200;
          mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 13, animate: false });
        }
      } catch { /* noop */ }
    }, 200);
    return () => clearTimeout(t);
  }, [visible, ready, items]);

  return (
    <div className={`relative ${className}`} data-testid="map-search-view">
      <div ref={mapDivRef} className="absolute inset-0" />

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-habita-cream text-sm text-gray-500">
          Cargando mapa…
        </div>
      )}

      {showSearchHere && (
        <button
          onClick={onSearchThisArea}
          data-testid="map-search-here-btn"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white shadow-xl rounded-full px-5 h-11 flex items-center gap-2 text-sm font-semibold text-habita-dark hover:bg-habita-mint hover:text-habita-dark transition-colors border border-gray-200"
        >
          <MapPin className="w-4 h-4 text-habita" />
          {loading ? 'Buscando…' : 'Buscar en esta zona'}
        </button>
      )}

      {popup?.property && (
        <MapPropertyCard
          property={popup.property}
          onClose={() => setPopup(null)}
          onOpen={() => navigate(`/propiedad/${popup.property.id}`)}
        />
      )}
    </div>
  );
}

/** Floating preview card shown when a marker is clicked. */
function MapPropertyCard({ property, onClose, onOpen }) {
  const photo = property.images?.[0];
  return (
    <div
      data-testid="map-popup-card"
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-white rounded-2xl shadow-2xl w-[300px] max-w-[95vw] overflow-hidden border border-gray-200"
    >
      <button
        onClick={onClose}
        className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow"
        aria-label="Cerrar"
        data-testid="map-popup-close-btn"
      >
        <X className="w-4 h-4" />
      </button>
      {photo && (
        <div className="aspect-[16/10] relative overflow-hidden bg-habita-cream">
          <img src={photo} alt={property.title} className="w-full h-full object-cover" />
          <div className="absolute bottom-2 left-2 bg-white/95 text-habita-dark text-xs uppercase tracking-widest font-bold px-2 py-1 rounded">
            {property.operation === 'venta' ? 'VENTA' : 'ARRIENDO'}
          </div>
        </div>
      )}
      <div className="p-3">
        <p className="text-base font-bold text-habita-dark">{formatCOP(property.price)}{property.operation === 'arriendo' && '/mes'}</p>
        <p className="text-[13px] text-gray-700 font-medium mt-0.5 line-clamp-1">{property.title}</p>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{property.neighborhood ? `${property.neighborhood}, ` : ''}{property.city}</p>
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
          <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {property.beds} hab</span>
          <span className="flex items-center gap-1"><Maximize className="w-3 h-3" /> {property.area} m²</span>
        </div>
        <Button
          onClick={onOpen}
          className="btn-habita w-full mt-3 h-10 rounded-full text-sm"
          data-testid="map-popup-open-btn"
        >
          Ver propiedad
        </Button>
      </div>
    </div>
  );
}

export const useBboxState = () => useMemo(() => ({}), []);
